import { isNull } from 'lodash';
import { isUndefined } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { Clause, ClauseTagDropdownOptions, ClientTag, FuzzyMatchTags } from '../../../clause-library/store/types';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { OpinionClause, OpinionInstance, OpinionInstanceType, SignOffConversation, SignOffOpinionInstance, SignOffOpinionInstanceContent, SignOffSmileyAndNote, TimelineOpinionInstance, UpdatedOpinionField } from './types';
import { checkAllPageRefsVerified, getUnverifiedSectionIds } from './utils';
import { getOpinionInstanceRoot } from '../../store/selectors';
import { establishNettingEngineFields } from '../../../constants/opinion';
import { getOpinion } from '../../my-opinions/store';

export const getSavedInstance = (state: ApplicationState): OpinionInstance | null => getOpinionInstanceRoot(state).savedInstance;

export const getCurrentInstance = (state: ApplicationState): OpinionInstance | null => getOpinionInstanceRoot(state).currentInstance;

export const getCurrentInstanceType = (state: ApplicationState): OpinionInstanceType | undefined => getOpinionInstanceRoot(state).currentInstance?.type;

export const getInstanceIncludesUnverifiedPageRefs = (state: ApplicationState): boolean => {
    const currentInstance = getCurrentInstance(state);
    return isNull(currentInstance) || !checkAllPageRefsVerified(currentInstance.content);
};

export const getAllFieldsUpdated = (state: ApplicationState): UpdatedOpinionField[] => getCurrentInstance(state)?.fieldsUpdated || [];

export const getAdditionalProvisionsUpdated = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).additionalProvisionsUpdated;

export const getSectionIdsUpdated = (state: ApplicationState): string[] => {
    const ADDITIONAL_PROVISIONS_SECTION_ID = 'additionalProvisions';
    const additionalProvisionsUpdated = getAdditionalProvisionsUpdated(state);
    let sectionIdsUpdated = getAllFieldsUpdated(state).map(({ sectionId }) => sectionId);
    if (additionalProvisionsUpdated && !sectionIdsUpdated.includes(ADDITIONAL_PROVISIONS_SECTION_ID)) {
        sectionIdsUpdated.push(ADDITIONAL_PROVISIONS_SECTION_ID);
    }
    return sectionIdsUpdated;
};

export const getSectionIdsWithUnverifiedPageRefs = (state: ApplicationState): string[] => {
    const currentInstance = getCurrentInstance(state);
    return !isNull(currentInstance) ? getUnverifiedSectionIds(currentInstance.content) : [];
};

export const getSectionUpdated = (sectionId: string) => (state: ApplicationState): boolean => getSectionIdsUpdated(state).includes(sectionId);

export const getSectionIncludesUnverifiedPageRefs = (sectionId: string) => (state: ApplicationState): boolean => getSectionIdsWithUnverifiedPageRefs(state).includes(sectionId);

export const getSavedSignOffNotes = (state: ApplicationState): SignOffOpinionInstance | null => getOpinionInstanceRoot(state).savedSignOff;

export const getCurrentSignOffNotes = (state: ApplicationState): SignOffOpinionInstance | null => getOpinionInstanceRoot(state).currentSignOff;

export const getSignOffNotesContent = (state: ApplicationState): SignOffSmileyAndNote => getOpinionInstanceRoot(state).signOffNotesContent;

export const getFieldSignOffNotes = (section: string, field?: string) => (state: ApplicationState): SignOffConversation | undefined => {
    if (['counterpartyCoverage', 'transactionCoverage', 'agreementCoverage', 'additionalProvisions', 'annexCoverage'].includes(section)) {
        const notes = getCurrentSignOffNotes(state)?.content[section as keyof SignOffOpinionInstanceContent];
        if (!isUndefined(notes)) {
            return notes as SignOffConversation;
        }
        return notes;
    }
    /* eslint-disable @typescript-eslint/no-explicit-any */
    return (getCurrentSignOffNotes(state)?.content[section as keyof SignOffOpinionInstanceContent] as any)[field!];
};

export const getSavedFieldSignOffNotes = (section: string, field?: string) => (state: ApplicationState): SignOffConversation | undefined => {
    if (['counterpartyCoverage', 'transactionCoverage', 'agreementCoverage', 'additionalProvisions', 'annexCoverage'].includes(section)) {
        const notes = getSavedSignOffNotes(state)?.content[section as keyof SignOffOpinionInstanceContent];
        if (!isUndefined(notes)) {
            return notes as SignOffConversation;
        }
        return notes;
    }
    /* eslint-disable @typescript-eslint/no-explicit-any */
    return (getSavedSignOffNotes(state)?.content[section as keyof SignOffOpinionInstanceContent] as any)[field!];
};

export const getIsEditingSignOff = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).isEditingSignOff;

export const getIsEditingNote = (state: ApplicationState): boolean => !isNull(getOpinionInstanceRoot(state).indexOfUpdatedNote);

export const getSignOffInstanceUpdated = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).signOffInstanceUpdated;

export const getSignOffNotesModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).signOffNotesModalOpen;

export const getSectionOrFieldId = (state: ApplicationState): string => getOpinionInstanceRoot(state).sectionOrFieldId;

export const getSignOffModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).signOffModalOpen;

export const getOpinionInstanceTimeline = (state: ApplicationState): TimelineOpinionInstance[] => getOpinionInstanceRoot(state).timeline;

export const getIsFirstTimelineEntry = (state: ApplicationState): boolean => {
    const timeline = getOpinionInstanceTimeline(state);
    const savedOpinion = getSavedInstance(state);
    return !isNull(savedOpinion) && timeline[0].opinionInstanceId === savedOpinion.opinionInstanceId;
};

export const getOpenFieldsAndSections = (state: ApplicationState): string[] => getOpinionInstanceRoot(state).openFieldsAndSections;

export const getIsEditing = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).isEditing;

export const getIsUpdating = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).isUpdating;

export const getIsSaving = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).isSaving;

export const getIsLoading = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).isLoading;

export const getInstanceUpdated = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).instanceUpdated;

export const getUpdatedInstanceModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).updatedInstanceModalOpen;

export const getConfirmSaveModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).confirmSaveModalOpen;

export const getLinkedOpinionModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).linkedOpinionModalOpen;

export const getSupportOpinionUploadModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).supportOpinionUploadModalOpen;

export const getSubCounterpartyTypeModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).subCounterpartyTypeModalOpen;

export const getCurrentInstanceOpinionId = (state: ApplicationState): number | null => getCurrentInstance(state)?.opinionId || null;

export const getNettingEngineFields = (state: ApplicationState): string[] => {
    const type = getCurrentInstanceType(state);
    const opinion = getOpinion(state);
    const isIndustryStandard = opinion?.bespoke === 0;
    return establishNettingEngineFields(type, isIndustryStandard);
};

export const getShowUpdatedOnly = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).showUpdatedOnly;

// Opinion Clauses

export const getAllOpinionClauses = (state: ApplicationState): OpinionClause[] => getOpinionInstanceRoot(state).opinionClauses;

export const getAllOpinionClauseIds = (state: ApplicationState): number[] => getOpinionInstanceRoot(state).allOpinionClauseIds;

export const getOpinionClauseTableIsLoading = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).clauseTableLoading;

export const getOpinionClauseModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).clauseModalOpen;

export const getOpinionClause = (state: ApplicationState): Clause => getOpinionInstanceRoot(state).clause;

export const getOpinionClauseDescriptionOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).clauseDescriptionOpen;

export const getCurrentOpinionClause = (state: ApplicationState): Clause | null => getOpinionInstanceRoot(state).currentClause;

export const getOpinionClauseTagTerm = (state: ApplicationState): string => getOpinionInstanceRoot(state).tagTerm;

export const getOpinionFuzzyMatchResults = (state: ApplicationState): FuzzyMatchTags[] => getOpinionInstanceRoot(state).fuzzyMatchResults;

export const getOpinionFuzzyMatchModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).fuzzyMatchModalOpen;

export const getOpinionClauseLibraryDropdownOptions = (state: ApplicationState): ClauseTagDropdownOptions => getOpinionInstanceRoot(state).clauseLibraryDropdownOptions;

export const getAllOpinionClauseLibraryTags = (state: ApplicationState): string[] => {
    const { agreementTypeOptions, jurisdictionOptions, counterpartyTypeOptions, productTypeOptions, provisionTypeOptions, miscellaneousOptions, opinionsOptions } = getOpinionClauseLibraryDropdownOptions(state);
    const getOptionValues = (options: DropdownOption[]) => options.map(({ value }) => value.toLowerCase());
    return [...getOptionValues(agreementTypeOptions), ...getOptionValues(jurisdictionOptions), ...getOptionValues(counterpartyTypeOptions), ...getOptionValues(productTypeOptions), ...getOptionValues(provisionTypeOptions), ...getOptionValues(miscellaneousOptions), ...getOptionValues(opinionsOptions)];
};

export const getAllOpinionClientTags = (state: ApplicationState): ClientTag[] => getOpinionInstanceRoot(state).allClientTags;

export const getOpinionClauseCanSave = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).clauseCanSave;

export const getExistingClausesModalOpen = (state: ApplicationState): boolean => getOpinionInstanceRoot(state).linkOpinionClauseModalOpen;

export const getAgreementCoverageSearchTerm = (state: ApplicationState): string => getOpinionInstanceRoot(state).agreementCoverageSearchTerm;

export const getCurrentInstanceIsSignedOff = (state: ApplicationState): boolean => {
    const savedSignOff = getSavedSignOffNotes(state);
    return savedSignOff ? !isNull(savedSignOff.approvedBy) : false;
};

// Clause Table Filters and Pagination
export const getOpinionClausesPageNumber = (state: ApplicationState): number => getOpinionInstanceRoot(state).opinionClausePage;
export const getTotalOpinionClauses = (state: ApplicationState): number => getOpinionInstanceRoot(state).totalOpinionClauses;
export const getOpinionClauseFilters = (state: ApplicationState): TableFilters => getOpinionInstanceRoot(state).opinionClauseFilters;
export const getOpinionClauseColumnSort = (state: ApplicationState): ColumnSort | undefined => getOpinionInstanceRoot(state).opinionClauseSort;
